import React, { useEffect, useState } from "react";
import { Box, Flex, Skeleton, SkeletonCircle, Table, Tbody, Td, Text, Th, Thead, Tr, VisuallyHidden } from "@chakra-ui/react";
import { useReducedMotion } from "../../theme/foundations/motion";
import { DataTable } from "./DataTable";
import { BrandMark } from "./BrandMark";

export interface TableSkeletonProps {
  /** Real column headings — rendered in a real <Thead> so widths do not jump when data arrives. */
  columns: string[];
  /** Headings that stay named for screen readers but are visually hidden, e.g. an "Actions" column. */
  hiddenColumns?: string[];
  rows?: number;
  size?: "sm" | "md";
}

const WIDTHS = [70, 50, 35, 35, 45, 30];

/**
 * Loading state for a DataTable: the real header plus `rows` rows of deterministic-width skeleton bars.
 * @example {isPending ? <TableSkeleton columns={["Case No.", "Subject", "Created", "Status"]} rows={5} /> : <DataTable>…</DataTable>}
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({ columns, hiddenColumns, rows = 5, size = "md" }) => (
  <Box aria-busy="true">
    <VisuallyHidden>Loading</VisuallyHidden>
    <DataTable size={size}>
      <Table size={size}>
        <Thead>
          <Tr>
            {columns.map((c) => (
              <Th key={c} scope="col">
                {hiddenColumns?.includes(c) ? <VisuallyHidden>{c}</VisuallyHidden> : c}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <Tr key={r}>
              {columns.map((c, i) => (
                <Td key={c}>
                  <Skeleton speed={1.2} h="1rem" w={`${WIDTHS[(i + r) % WIDTHS.length]}%`} />
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </DataTable>
  </Box>
);

export interface StatSkeletonProps {
  size?: "kpi" | "hero";
}

/** Loading state for a StatCard: icon circle + label, value and caption bars, in the real card frame. */
export const StatSkeleton: React.FC<StatSkeletonProps> = ({ size = "kpi" }) => (
  <Box layerStyle="card" p={5} aria-busy="true" minH={size === "hero" ? "10.5rem" : "8.5rem"}>
    <VisuallyHidden>Loading</VisuallyHidden>
    <Flex align="center" gap={3} mb={4}>
      <SkeletonCircle speed={1.2} size="36px" />
      <Skeleton speed={1.2} h="0.75rem" w="40%" />
    </Flex>
    <Skeleton speed={1.2} h={size === "hero" ? "2.75rem" : "1.75rem"} w="60%" mb={3} />
    <Skeleton speed={1.2} h="0.75rem" w="50%" />
  </Box>
);

export interface BrandSplashProps {
  /** Rotating status copy (1.5 s each). */
  messages?: string[];
}

const DEFAULT_MESSAGES = ["Preparing your workspace…", "Fetching your branch…", "Almost there…"];

/**
 * Full-viewport branded loader for the app shell (replaces the centred Spinner). `role="status"`, polite live region.
 * Under prefers-reduced-motion: static logo, static half-filled bar, first message only.
 * @example if (isPending || !userData) return <BrandSplash />;
 */
export const BrandSplash: React.FC<BrandSplashProps> = ({ messages = DEFAULT_MESSAGES }) => {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduce || messages.length < 2) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % messages.length), 1500);
    return () => window.clearInterval(id);
  }, [reduce, messages.length]);

  return (
    <Flex
      role="status"
      aria-live="polite"
      layerStyle="sidebar"
      minH="100vh"
      w="full"
      direction="column"
      align="center"
      justify="center"
      gap={6}
      px={6}
    >
      <BrandMark variant="dark" h="3rem" className={reduce ? undefined : "mbf-breathe"} />
      <Box w="160px" h="4px" bg="rgba(255,255,255,0.15)" borderRadius="full" overflow="hidden" position="relative" aria-hidden>
        <Box
          position="absolute"
          top={0}
          left={0}
          h="full"
          w={reduce ? "50%" : "48px"}
          bg="leaf.500"
          borderRadius="full"
          className={reduce ? undefined : "mbf-sweep"}
        />
      </Box>
      <Text textStyle="caption" color="navy.200" minH="1.2em">
        {messages[reduce ? 0 : index]}
      </Text>
    </Flex>
  );
};
